/**
 * /audit — external-auditor read-only surface (AUDITOR-01, 2026-06-10).
 *
 * The landing surface for an accepted `external-auditor`. A firm admin grants a
 * named auditor TIME-BOXED, SCOPED, READ-ONLY access (POST /v1/admin/external-
 * auditors): the grant provisions a UserDoc with `role: 'external-auditor'`
 * (a role in ZERO backend write role-sets) and adds the auditor to each granted
 * trust's `memberIds`. Because they are in `memberIds`, the EXISTING read hooks
 * (`useTrusts`, `useTrust`, `useTrustDocuments`, `useDashboardHoldings`,
 * audit export) return exactly their granted trusts — so this surface is a lean
 * read-only MIRROR of the trustee read screens, built from the same real hooks,
 * NOT a fork of the trustee app.
 *
 * HONESTY (a regulator reads this): this surface exposes ONLY read controls
 * (view / download / export). It omits — does not merely disable — every write
 * affordance (upload, edit, distribute, resolution-sign), so the auditor is
 * never shown a control that would 403 server-side or imply write access.
 *
 * Gates, in order (mirrors routes/app/_layout.tsx):
 *   1. No session            → /auth/login.
 *   2. MFA step-up pending   → /auth/mfa-verify (fail-CLOSED on a check error).
 *   3. Wrong role            → landingRouteForRole(role) (a trustee/admin who
 *      somehow hits /audit is bounced to their own surface — /audit is
 *      auditor-only, mirroring how /admin bounces non-admins).
 * Server-side gates are the real backstop; these just keep the surfaces clean.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router';
import { useSession } from '../../lib/use-session';
import { canAccess } from '../../lib/authz/route-policy';
import { supabase } from '../../lib/supabase';
import type { UserRole } from '../../lib/api';

export const Route = createFileRoute('/audit')({
  component: AuditLayout,
});

/** Where a NON-auditor who lands on /audit is sent. Inlined (not imported from
 *  auth-landing.ts, a route-adjacent module) to keep this gate self-contained,
 *  but mirrors landingRouteForRole's mapping: firm staff → /admin/dashboard,
 *  everyone else → /app/dashboard (both target the real dashboard, not the bare
 *  blank layout route). */
const ADMIN_LANDING_ROLES = new Set<string>(['admin', 'compliance', 'super_admin']);
function landingRouteForNonAuditor(role: string): '/admin/dashboard' | '/app/dashboard' {
  return ADMIN_LANDING_ROLES.has(role) ? '/admin/dashboard' : '/app/dashboard';
}

type AalState =
  | { status: 'checking' }
  | { status: 'ok' }
  | { status: 'needs-step-up' };

function AuditLayout() {
  const { session, role, loading } = useSession();
  const [aal, setAal] = useState<AalState>({ status: 'checking' });

  useEffect(() => {
    if (loading || !session) {
      setAal({ status: 'checking' });
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (cancelled) return;
      if (error || !data) {
        // Fail CLOSED — same rationale as routes/app/_layout.tsx.
        setAal({ status: 'needs-step-up' });
        return;
      }
      const needsStepUp = data.currentLevel === 'aal1' && data.nextLevel === 'aal2';
      setAal({ status: needsStepUp ? 'needs-step-up' : 'ok' });
    })();
    return () => {
      cancelled = true;
    };
  }, [session, loading]);

  if (loading) return null;

  if (!session) {
    return <Navigate to="/auth/login" />;
  }

  // Role gate (S-2.1): /audit is auditor-only (allow-set from the central
  // route-policy). A trustee/admin who reaches it is sent to their own surface
  // (never shown a 403). Auditors fall through to the shell.
  if (!canAccess('audit', role)) {
    return <Navigate to={landingRouteForNonAuditor(role)} />;
  }

  if (aal.status === 'checking') return null;

  if (aal.status === 'needs-step-up') {
    return <Navigate to="/auth/mfa-verify" />;
  }

  return <Outlet />;
}

// Re-export so callers can type-narrow against the same union the gate uses.
export type { UserRole };
